import React from 'react';
import { getInlineEmbed } from '../lib/media-embed';

/**
 * Renders an inline iframe player for any URL whose host supports
 * iframe embedding (YouTube, Spotify, Apple Music, SoundCloud). Returns
 * `null` for non-embeddable URLs so the caller can fall back to its
 * thumbnail-with-play-button pattern.
 *
 * Used in BookmarkDetail (owner view) and SharePage (public view) so
 * both render the same player in the same place.
 *
 * Props:
 *   url            - the bookmark's source URL
 *   spotifyEmbedUrl - backend-resolved Spotify embed URL, if any (overrides URL parsing)
 *   title          - bookmark title (used for iframe `title` a11y attribute)
 *   className      - extra classes for the outer wrapper
 *   testIdPrefix   - prefix for data-testid attributes (e.g. 'share' → 'share-youtube-player')
 */
const InlineMediaPlayer = ({ url, spotifyEmbedUrl, tiktokVideoId, title, className = '', testIdPrefix = 'inline' }) => {
    const embed = getInlineEmbed(url, spotifyEmbedUrl, tiktokVideoId);
    if (!embed) return null;

    const wrapper = `mb-5 rounded-2xl overflow-hidden border border-border/60 ${className}`;
    const iframeTitle = title || 'Embedded media';

    if (embed.kind === 'youtube') {
        return (
            <div className={`${wrapper} bg-black`} data-testid={`${testIdPrefix}-video-player-container`}>
                <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                    <iframe
                        src={embed.src}
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                        frameBorder="0"
                        allow="autoplay; encrypted-media; picture-in-picture"
                        allowFullScreen
                        title={iframeTitle}
                        data-testid={`${testIdPrefix}-youtube-player`}
                    />
                </div>
            </div>
        );
    }

    if (embed.kind === 'spotify') {
        return (
            <div className={`${wrapper} bg-gradient-to-b from-[#1a1a2e] to-[#16213e]`} data-testid={`${testIdPrefix}-spotify-player-container`}>
                <iframe
                    src={embed.src}
                    width="100%"
                    height={embed.height}
                    frameBorder="0"
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy"
                    title={iframeTitle}
                    className="rounded-2xl"
                    data-testid={`${testIdPrefix}-spotify-player`}
                />
            </div>
        );
    }

    if (embed.kind === 'apple-music') {
        // Apple Music's official embed widget. `allow="autoplay *"` is
        // required by their widget to permit preview playback; without
        // it the play button no-ops in some Chromium builds.
        return (
            <div className={`${wrapper} bg-[#fafafa]`} data-testid={`${testIdPrefix}-apple-music-player-container`}>
                <iframe
                    src={embed.src}
                    width="100%"
                    height={embed.height}
                    frameBorder="0"
                    allow="autoplay *; encrypted-media *; fullscreen *; clipboard-write"
                    sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
                    loading="lazy"
                    title={iframeTitle}
                    className="rounded-2xl"
                    data-testid={`${testIdPrefix}-apple-music-player`}
                />
            </div>
        );
    }

    if (embed.kind === 'tiktok') {
        // TikTok's official /player/v1/ external iframe embed (not
        // /embed/v2/, which is the in-app widget that autoplays and
        // swipe-skips to the next video on tap). `allow-scripts` is required
        // for the video player controls to hydrate; without it the
        // iframe renders a static poster only.
        return (
            <div className={`${wrapper} bg-black flex justify-center`} data-testid={`${testIdPrefix}-tiktok-player-container`}>
                <iframe
                    src={embed.src}
                    width="325"
                    height={embed.height}
                    frameBorder="0"
                    allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                    scrolling="no"
                    loading="lazy"
                    title={iframeTitle}
                    style={{ maxWidth: '100%' }}
                    data-testid={`${testIdPrefix}-tiktok-player`}
                />
            </div>
        );
    }

    if (embed.kind === 'soundcloud') {
        return (
            <div className={`${wrapper} bg-[#f5f5f0]`} data-testid={`${testIdPrefix}-soundcloud-player-container`}>
                <iframe
                    src={embed.src}
                    width="100%"
                    height={embed.height}
                    frameBorder="0"
                    scrolling="no"
                    allow="autoplay"
                    loading="lazy"
                    title={iframeTitle}
                    className="rounded-2xl"
                    data-testid={`${testIdPrefix}-soundcloud-player`}
                />
            </div>
        );
    }

    return null;
};

export default InlineMediaPlayer;
